import { createAsyncThunk } from "@reduxjs/toolkit";

import { services } from "../../services";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";

type TAnalysisTarget = string | { tapeId: string; dataset: boolean };

const resolveTarget = (target: TAnalysisTarget) =>
	typeof target === "string"
		? { tapeId: target, dataset: false }
		: target;

export const getTapeDataForAnalysisThunk = createAsyncThunk(
	"analysis/getTapeDataForAnalysisThunk",
	async (target: TAnalysisTarget, { rejectWithValue }) => {
		const { tapeId, dataset } = resolveTarget(target);
		try {
			const data =
				await services.analysisServiceInstance.getTapeDataForAnalysis(
					tapeId,
					dataset
				);
			return data;
		} catch (error: unknown) {
			return rejectWithValue(
				getApiErrorMessage(error, "Unable to load Caliper runs.")
			);
		}
	}
);

export const getTapeStatsThunk = createAsyncThunk(
	"analysis/getTapeStatsThunk",
	async (target: TAnalysisTarget) => {
		const { tapeId, dataset } = resolveTarget(target);
		if (dataset) {
			const response = await services.ledgerService.getDatasetStats(tapeId);
			return response.data?.stats ?? null;
		}
		return await services.analysisServiceInstance.getTapeStats(tapeId);
	}
);

export const getPointByRunIdThunk = createAsyncThunk(
	"analysis/getPointByRunIdThunk",
	async ({
		runId,
		tapeId,
		dataset = false,
	}: {
		runId: string;
		tapeId: string;
		dataset?: boolean;
	}) => {
		const data =
			await services.analysisServiceInstance.getPointByRunId(
				runId,
				tapeId,
				dataset
			);
		return data;
	}
);

export const getQuestionsForAnalysisThunk =
	createAsyncThunk(
		"analysis/getQuestionsForAnalysisThunk",
		async (pointId: string) => {
			const data =
				await services.analysisServiceInstance.getPointQuestions(
					pointId
				);
			return data;
		}
	);

export const getSimulationQuestionsThunk = createAsyncThunk(
	"analysis/getSimulationQuestionsThunk",
	async (runId: string) => {
		const data =
			await services.analysisServiceInstance.getSimulationQuestions(
				runId
			);
		return data;
	}
);
